import { LoadingSpinner } from "@/components/LoadingSpinner"
import { countryCodeInfo, supportedCountryCodes } from "@/lib/locales"
import { VibratoClient, VoiceClone } from "@/lib/vibrato-api"
import { Modal, ModalBody, ModalFooter } from "flowbite-react"
import { Clock, Database, MicrophoneSpeaking, NavArrowRight, Phone, Play } from "iconoir-react"
import { useContext, useEffect, useState } from "react"
import { VibratoClientContext } from "./contexts"

export default function CloneVoiceModal({
  show,
  setShow,
  reload
}: {
  show: boolean
  setShow: (show: boolean) => void
  reload: () => void
}) {
  const vibratoClient: VibratoClient = useContext(VibratoClientContext)
  const [voiceClone, setVoiceClone] = useState<VoiceClone | null>(null)
  const [errorMessage, setErrorMessage] = useState("")
  const [countryCode, setCountryCode] = useState("1")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [step, setStep] = useState("requirements")
  const [isCreating, setIsCreating] = useState(false)

  const onClose = () => {
    setShow(false)
    setErrorMessage("")
    setStep("requirements")
    setPhoneNumber("")
  }

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(event.target.value)
  }

  const handleCountryCodeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCountryCode(event.target.value)
  }

  const handlePlayVoice = () => {
    if (!voiceClone?.sample_audio_url) return
    const audio = new Audio(voiceClone.sample_audio_url)
    audio.play()
  }

  const handleSubmit = () => {
    const strippedPhoneNumber = phoneNumber.replace(/\D/g, "")
    const strippedCountryCode = countryCode.replace(/\D/g, "")
    if (strippedPhoneNumber.length !== 10) {
      setErrorMessage("Please enter a valid 10-digit phone number.")
      return
    }

    setErrorMessage("")
    setIsCreating(true)

    vibratoClient
      .createVoiceClone({})
      .then((response) => {
        setVoiceClone(response)
        const id = response.id
        if (!id) {
          setErrorMessage("An error occurred while creating the voice clone.")
          setIsCreating(false)
          return
        }
        vibratoClient
          .updateVoiceClone(id, { phone_number: strippedPhoneNumber, country_code: strippedCountryCode })
          .then(() =>
            vibratoClient
              .executeVoiceClone(id)
              .then(() => {
                setStep("creation")
                setIsCreating(false)
              })
              .catch((error) => {
                setErrorMessage("An error occurred while creating the voice clone.")
                setIsCreating(false)
                console.error(error)
              })
          )
          .catch((error) => {
            setErrorMessage("An error occurred while creating the voice clone.")
            setIsCreating(false)
            console.error(error)
          })
      })
      .catch((error) => {
        setErrorMessage("An error occurred while creating the voice clone.")
        setIsCreating(false)
        console.error(error)
      })
  }

  const handleContinueToPhoneNumber = () => {
    setStep("form")
  }

  useEffect(() => {
    let intervalId: any

    if (voiceClone && show) {
      intervalId = setInterval(() => {
        vibratoClient
          .getVoiceClone(voiceClone?.id || 0)
          .then((updatedClone) => {
            setVoiceClone(updatedClone)

            if (updatedClone.status === "call_completed") {
              setStep("waiting")
            }
            if (updatedClone.status === "voice_clone_failed") {
              setStep("failed")
              clearInterval(intervalId)
            }
            if (updatedClone.status === "voice_clone_errored") {
              setStep("failed")
              clearInterval(intervalId)
            }
            if (updatedClone.status === "voice_clone_finished") {
              setStep("finished")
              reload()
              clearInterval(intervalId)
            }
          })
          .catch((error) => {
            console.error("Failed to reload voice clone:", error)
          })
      }, 5000)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [show, voiceClone, vibratoClient])

  const showSubmit = step === "form"

  return (
    <>
      <Modal show={show} onClose={onClose} size="3xl" className="pt-safe">
        <ModalBody className="p-0">
          <div className="flex flex-col">
            {/* Header Section with Gradient Background */}
            <div className="bg-linear-to-r from-purple-600 to-indigo-600 text-white p-8 rounded-t-lg">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-white/20 p-3 rounded-full">
                  <MicrophoneSpeaking className="w-8 h-8" />
                </div>
              </div>
              <h2 className="text-3xl font-bold text-center mb-2">Create Your AI Voice Clone</h2>
              <p className="text-center text-white/80">Transform your voice into a powerful AI assistant</p>
            </div>

            <div className="p-8">
              {step === "requirements" && (
                <div className="space-y-6">
                  <div className="bg-linear-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Before We Begin</h3>
                    <p className="text-gray-600 mb-6">
                      Please ensure you meet the following requirements for the best voice cloning experience:
                    </p>

                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <span className="p-1.5 bg-purple-100 rounded-full shrink-0">
                          <MicrophoneSpeaking className="w-5 h-5 text-purple-600" />
                        </span>
                        <div>
                          <p className="font-medium text-gray-900">Quiet Environment</p>
                          <p className="text-sm text-gray-500">
                            Find a space free from background noise, echoes, and interruptions
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <span className="p-1.5 bg-purple-100 rounded-full shrink-0">
                          <Phone className="w-5 h-5 text-purple-600" />
                        </span>
                        <div>
                          <p className="font-medium text-gray-900">Phone Ready</p>
                          <p className="text-sm text-gray-500">Ensure your phone is charged and has good reception</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <span className="p-1.5 bg-purple-100 rounded-full shrink-0">
                          <Clock className="w-5 h-5 text-purple-600" />
                        </span>
                        <div>
                          <p className="font-medium text-gray-900">Time Commitment</p>
                          <p className="text-sm text-gray-500">
                            The recording process takes about 5 minutes to complete
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <span className="p-1.5 bg-purple-100 rounded-full shrink-0">
                          <Database className="w-5 h-5 text-purple-600" />
                        </span>
                        <div>
                          <p className="font-medium text-gray-900">Process Overview</p>
                          <p className="text-sm text-gray-500">
                            You&apos;ll receive a phone call and will need to read some text
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === "form" && (
                <div className="space-y-6">
                  <div className="flex flex-col gap-4">
                    <h4 className="text-lg font-semibold text-gray-900">Enter Your Phone Number</h4>
                    <div className="flex flex-wrap gap-2">
                      <div className="">
                        <label htmlFor="country-code" className="block text-sm font-medium text-gray-700 mb-1">
                          Country Code
                        </label>
                        <select
                          id="country-code"
                          className="form-select w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                          value={countryCode}
                          onChange={handleCountryCodeChange}
                        >
                          {supportedCountryCodes.map((country) => (
                            <option key={country} value={countryCodeInfo[country].code}>
                              {countryCodeInfo[country].displayName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="">
                        <label htmlFor="phone-number" className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone-number"
                          className="form-input w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                          placeholder="(123) 456-7890"
                          value={phoneNumber}
                          onChange={handlePhoneNumberChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === "creation" && (
                <div className="space-y-6">
                  <div className="bg-linear-to-br from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-100">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">📞 Incoming Call</h3>
                    <p className="text-gray-600 mb-4">
                      You&apos;ll receive a call within a minute. Our AI assistant will guide you through a quick
                      recording process:
                    </p>
                    <div className="space-y-4">
                      <div className="bg-white/50 p-4 rounded-lg border border-blue-100">
                        <p className="font-semibold text-blue-800 mb-2">Your Verification Code:</p>
                        <p className="text-2xl font-mono text-purple-600">{voiceClone?.code}</p>
                      </div>
                      <div className="bg-white/50 p-4 rounded-lg border border-blue-100">
                        <p className="font-semibold text-blue-800 mb-2">Sample Text to Read:</p>
                        <div className="space-y-3 text-gray-600">
                          <p>
                            In a big, green park full of playful kids, there lived a small, friendly dog named Sparky.
                            Sparky enjoyed playing catch with visitors, and one sunny day, he found a shiny, red ball by
                            the big oak tree. Deciding it was the perfect time to make a new friend, Sparky carried the
                            ball across the park.
                          </p>
                          <p>
                            He spotted Lily, a shy girl who often visited the park but rarely joined in the games.
                            Sparky approached her, dropped the ball at her feet, and wagged his tail. Lily smiled, threw
                            the ball far, and Sparky chased after it. They played until sunset, with Lily laughing more
                            than ever before. From then on, they became best friends, their laughter and joy of new
                            friendship filling the park.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === "waiting" && (
                <div className="text-center space-y-4">
                  <div className="animate-spin w-12 h-12 mx-auto">
                    <svg className="w-full h-full text-purple-600" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Processing Your Voice Clone</h3>
                  <p className="text-gray-600">
                    Please wait while we create your AI voice. This usually takes 1-2 minutes.
                  </p>
                </div>
              )}

              {step === "failed" && (
                <div className="text-center space-y-4">
                  <div className="w-12 h-12 mx-auto text-red-500">❌</div>
                  <h3 className="text-xl font-semibold text-gray-900">Voice Clone Failed</h3>
                  <p className="text-gray-600">
                    We couldn&apos;t create your voice clone. Please try again and ensure you&apos;re in a quiet
                    environment with clear speech.
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                    <p className="text-sm text-yellow-800 font-medium mb-2">
                      💡 Tip: Check if you have unknown numbers blocked
                    </p>
                    <p className="text-sm text-yellow-700 mb-3">
                      If you didn't receive our call, you might have unknown numbers blocked on your device.
                    </p>
                    <div className="flex justify-center gap-4">
                      <a
                        href="https://support.apple.com/en-us/111106"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        iOS Instructions (perform the opposite of the steps so that you receive the call from us)
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {step === "finished" && (
                <div className="space-y-6">
                  <div className="bg-linear-to-br from-green-50 to-blue-50 rounded-xl p-6 border border-green-100">
                    <div className="text-center mb-6">
                      <div className="w-12 h-12 mx-auto text-green-500 mb-4">✨</div>
                      <h3 className="text-2xl font-semibold text-gray-900">Voice Clone Created Successfully!</h3>
                      <p className="text-gray-600 mt-2">Your voice is now ready to use in all your Vibrato calls.</p>
                    </div>

                    <div className="flex justify-center">
                      <button
                        onClick={handlePlayVoice}
                        className="inline-flex items-center px-6 py-3 bg-purple-600 text-white text-lg font-semibold rounded-full hover:bg-purple-700 transition-colors gap-2"
                      >
                        Preview Your Voice
                        <Play className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {errorMessage && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600">{errorMessage}</p>
                </div>
              )}
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <div className="flex justify-end gap-3">
            {step === "requirements" && (
              <button
                onClick={handleContinueToPhoneNumber}
                className="inline-flex items-center px-6 py-2.5 bg-purple-600 text-white font-semibold rounded-full transition-colors gap-2 hover:bg-purple-700"
              >
                Continue
                <NavArrowRight className="w-5 h-5" />
              </button>
            )}
            {showSubmit && (
              <button
                onClick={handleSubmit}
                disabled={isCreating}
                className={`inline-flex items-center px-6 py-2.5 bg-purple-600 text-white font-semibold rounded-full transition-colors gap-2 ${
                  isCreating ? "opacity-70 cursor-not-allowed" : "hover:bg-purple-700"
                }`}
              >
                {isCreating ? (
                  <>
                    <LoadingSpinner className="w-5 h-5" />
                    Calling...
                  </>
                ) : (
                  <>
                    Submit
                    <NavArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-6 py-2.5 text-gray-600 font-semibold rounded-full hover:bg-gray-100 transition-colors"
            >
              {step !== "finished" ? "Cancel" : "Close"}
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  )
}
